// src/view/slowMo.ts — MİNİ AĞIR ÇEKİM (DESIGN §10) ve maç sonu yavaşlatması: SUNUM tarafı, three'siz, DOM'suz.
//
// NEDEN sim'den çıktı (NETWORK.md karar 4): ağır çekim eskiden `MatchState.slowMoUntil` idi ve `world.hash()`e
// giriyordu, yani DÜNYA DURUMUNUN parçasıydı. Ağda bu iki şeyi bozar:
//   • Tetik kuralı "vuran ya da yiyen `!isBot`" idi. Ağda uzak insan da `isBot:false` olduğu için iki yabancının
//     dövüşü HERKESİN ekranını yavaşlatırdı. Kuralın demek istediği "BENİM dövüşçüm işin içinde mi" idi ve bu
//     ancak istemcide ifade edilebilir — sim herkesin aynı dünyasıdır, "ben" diye biri yoktur.
//   • `timeScale` accumulator'a uygulandığı için ağır çekimdeki istemci saniyede 60'tan az tick üretir; sabit 60 Hz
//     akan bir sunucu karşısında geri kalır. Bu yüzden ağ maçında zaman genişletmesi KAPALI olacak; ağır çekimin
//     ağdaki karşılığı RENDER saatinin geride kalıp sonra yetişmesidir (NETWORK.md §2.1, FAZ 1).
//
// Sim artık yalnız GERÇEĞİ bildirir (`Ev.Downed`: "bu isabet yere serdi / takla attırdı"); kararı — kim izliyor, ne
// kadar sık, ne kadar uzun — burası verir. Saf ve durumu tek nesnede: Node'da test edilir, kamera ve game.ts okur.
import type { SlowMoState } from '../core/types.ts';
import { NO_ID, NO_TICK, TICK_HZ } from '../config/constants.ts';
import { clamp01, lerp, smoothstep } from '../core/math.ts';
import { T } from '../config/tuning.ts';

export type { SlowMoState };

export const createSlowMo = (): SlowMoState => ({ hitAt: NO_TICK, actor: NO_ID, victim: NO_ID, until: NO_TICK });

/**
 * `Ev.Downed` geldi: mini ağır çekim açılsın mı? Frenler (yoksa oyun sürekli ağır çeker):
 *   • yalnız DÖVÜŞ fazında (geri sayım ve maç sonu sunumu kendi zamanlamasına sahip),
 *   • **benim dövüşçüm işin içinde olmalı** — `involvesLocal`. Bot–bot ya da iki yabancının dövüşü ekranımı
 *     yavaşlatmaz (`?smoke=1` hiç tetiklenmez, ölçüm kirlenmez). Bu, eski `!isBot` kuralının ağ-doğru hâli.
 *   • son tetikten bu yana en az `knockdownSlowMoCooldownTicks` geçmiş olmalı.
 * true → pencere açıldı.
 */
export function requestHitSlowMo(s: SlowMoState, tick: number, actor: number, victim: number,
                                 inFight: boolean, involvesLocal: boolean): boolean {
  const dur = T.CAMERA.knockdownSlowMoTicks;
  if (dur <= 0 || !inFight || !involvesLocal) return false;
  if (s.hitAt !== NO_TICK && tick - s.hitAt < T.CAMERA.knockdownSlowMoCooldownTicks) return false;
  s.hitAt = tick;
  s.actor = actor; s.victim = victim;
  if (tick + dur > s.until || s.until === NO_TICK) s.until = tick + dur;   // max: açık bir pencereyi (maç sonu) kısaltma
  return true;
}

/** `Ev.MatchEnd`: kazanan sunumu için 2 s ×0.5 (DESIGN §13). Vuruş penceresinden bağımsız, sabit çarpanlı. */
export function requestMatchEndSlowMo(s: SlowMoState, tick: number): void {
  const until = tick + Math.round(T.CAMERA.matchEndSlowMo * TICK_HZ);
  if (s.until === NO_TICK || until > s.until) s.until = until;
}

/** Yavaşlatma isteği açık mı (vuruş ya da maç sonu). */
export const slowMoActive = (s: SlowMoState, tick: number): boolean => s.until !== NO_TICK && tick < s.until;

/**
 * ZAMAN EĞRİSİ (DESIGN §10, 5. tur) — pencere içindeki hız çarpanı. u = ilerleme 0..1:
 *   u ≤ 1 − easeOut → hold          (PLATO: vuruş sabit ×0.25 hızla AKAR — izlenmek istenen kısım burası)
 *   sonrası         → hold → 1.0    (smoothstep; son %35'te yumuşak çıkış)
 * Neden plato: 4. turdaki s(u) = deep + (1 − deep)·u² darbede neredeyse duruyor, sonra hızlanarak çıkıyordu →
 * kullanıcı "başlayınca duruyor, sonra hızlanıyor; ben yavaşça aksın istiyorum" dedi. Sabit hız, hareketi
 * okunabilir kılar; ivmelenen eğride hiçbir kare diğerine benzemediği için "ağır çekim" değil "takılma" okunuyordu.
 * Girişte rampa YOK: isabet karesinde zaten hitstop var, oraya BASAMAKLA inmek "çat" hissini verir; rampa
 * eklenirse darbenin kendi karesi tam hızda geçerdi. Azalmayan (geri sarma hissi vermez).
 */
export function hitSlowMoCurve(u: number): number {
  const hold = T.CAMERA.knockdownSlowMoHold;
  const out = T.CAMERA.knockdownSlowMoEaseOut;
  const c = clamp01(u);
  if (c >= 1) return 1;                       // pencere bitti: tam hız (easeOut 0 olsa bile basamakla kesilir)
  const flat = 1 - out;
  if (out <= 0 || c <= flat) return hold;
  return lerp(hold, 1, smoothstep(0, 1, (c - flat) / out));
}

/** Vuruş ağır çekimi penceresindeki ilerleme 0..1, pencere dışında −1 (kapalı). Kamera ve `timeScaleOf` ortak kullanır. */
export function hitSlowMoProgress(s: SlowMoState, tick: number): number {
  const n = T.CAMERA.knockdownSlowMoTicks;
  if (n <= 0 || s.hitAt === NO_TICK) return -1;
  const el = tick - s.hitAt;
  return el >= 0 && el < n ? el / n : -1;
}

/**
 * game.ts `timeScale()`in tamamı (saf; DOM'suz test edilebilir): vuruş penceresi içindeyse EĞRİ, dışarıdaysa açık
 * yavaşlatma (maç bitişi) çarpanı, hiçbiri yoksa tam 1.0.
 *
 * ÇEVRİMDIŞINDA bu çarpan accumulator'a uygulanır (sim saniyede 60'tan az tick üretir) — bugünkü his kare kare aynı.
 * AĞ MAÇINDA uygulanmaz: sim saati sunucuya kilitlidir. Ağır çekimin ağdaki karşılığı render saatinin geride kalıp
 * sonra yetişmesidir (NETWORK.md §2.1); o iş FAZ 1'in yetişme kipiyle birlikte gelir.
 */
export function timeScaleOf(s: SlowMoState, tick: number): number {
  if (!slowMoActive(s, tick)) return 1;
  const u = hitSlowMoProgress(s, tick);
  return u >= 0 ? hitSlowMoCurve(u) : T.CAMERA.slowMoScale;
}
